import { Source, Layer } from 'react-map-gl';
import { useEffect, useState } from 'react';
import arrowIcon1 from '../assets/arrow-up-type-1.png';
import arrowIcon2 from '../assets/arrow-up-type-2.png';
import arrowIcon3 from '../assets/arrow-up-type-3.png';
import { colors } from '../utils/colorDict';

const COLLECTION = process.env.REACT_APP_COLLECTION || null;

const arrowIcons = [arrowIcon1, arrowIcon2, arrowIcon3];

// Year-color gradient for the unified site: light teal (Scholten-ish) → very dark navy (Aleksandrowicz-ish)
const YEAR_COLOR_FROM = { r: 0x1a, g: 0xb8, b: 0xda };  // #1AB8DA — light teal/cyan
const YEAR_COLOR_TO   = { r: 0x0d, g: 0x2c, b: 0x40 };  // #0D2C40 — very dark navy
const YEAR_BIN_COUNT = 8; // number of color stops along the gradient

function lerp(a, b, t) {
  return Math.round(a + (b - a) * t);
}

function colorForBin(idx, total) {
  const t = total <= 1 ? 0 : idx / (total - 1);
  return {
    r: lerp(YEAR_COLOR_FROM.r, YEAR_COLOR_TO.r, t),
    g: lerp(YEAR_COLOR_FROM.g, YEAR_COLOR_TO.g, t),
    b: lerp(YEAR_COLOR_FROM.b, YEAR_COLOR_TO.b, t),
  };
}

// Recolor an arrow PNG so all non-near-white pixels are tinted to {tr,tg,tb}
function recolorArrow(image, { r: tr, g: tg, b: tb }) {
  const canvas = document.createElement('canvas');
  canvas.width = 24; canvas.height = 24;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(image, 0, 0, 24, 24);
  const imageData = ctx.getImageData(0, 0, 24, 24);
  const d = imageData.data;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] === 0) continue;
    const brightness = (d[i] + d[i + 1] + d[i + 2]) / 3;
    if (brightness < 200) {
      const ratio = brightness / 128;
      d[i]     = Math.min(255, Math.round(tr * ratio));
      d[i + 1] = Math.min(255, Math.round(tg * ratio));
      d[i + 2] = Math.min(255, Math.round(tb * ratio));
    }
  }
  return new Uint8Array(d.buffer);
}

const PointLayer = ({ filteredItems, mapRef, markedItemId, yearBounds }) => {
    // Track whether all per-bin icons are loaded so we can switch the layer to year-bin mode
    const [yearIconsReady, setYearIconsReady] = useState(false);
    // Year-binned icon coloring is only worth it on the unified site, where
    // colors visually distinguish collections by date. Per-collection sites
    // have a single creator, so the bins reduce to the same color.
    const useYearBins = !COLLECTION && Array.isArray(yearBounds) && yearBounds[0] != null && yearBounds[1] != null && yearBounds[1] > yearBounds[0];
    // The density heatmap is useful on every site — Scholten + Aleksandrowicz
    // also have areas of dense overlap (Tel Aviv / Jerusalem) at low zoom.
    // Gate only on yearBounds being loaded so we don't render the heatmap
    // before the page has a usable year range.
    const useHeatmap = Array.isArray(yearBounds) && yearBounds[0] != null && yearBounds[1] != null && yearBounds[1] > yearBounds[0];

    useEffect(() => {
        const map = mapRef.current.getMap();

        // (Re)load every icon — both the 3 simple ones and (on the unified site) the
        // year-binned variants. Mapbox style swaps wipe addImage()'d sprites, so this
        // also runs on each subsequent `style.load`.
        const loadIcons = () => {
            // 1) Simple single-color icons used by per-collection sites and as fallback.
            arrowIcons.forEach((iconSrc, index) => {
                const iconName = `arrowIcon${index + 1}`;
                if (map.hasImage(iconName)) return;
                const image = new Image(24, 24);
                image.src = iconSrc;
                image.onload = () => {
                    if (map.hasImage(iconName)) return;
                    const tc = parseInt(colors[1].slice(1), 16);
                    const tr = (tc >> 16) & 255, tg = (tc >> 8) & 255, tb = tc & 255;
                    map.addImage(iconName, { width: 24, height: 24, data: recolorArrow(image, { r: tr, g: tg, b: tb }) });
                };
            });

            // 2) Unified site: also load N year-binned icon variants per mediatype.
            if (!useYearBins) return;
            // Reset readiness so the layer falls back to the simple icons until all
            // bin icons are back. Mapbox style swap dropped them; re-add and flip the flag.
            setYearIconsReady(false);
            let loadedCount = 0;
            const totalToLoad = YEAR_BIN_COUNT * arrowIcons.length;
            arrowIcons.forEach((iconSrc, mediaIdx) => {
                for (let bin = 0; bin < YEAR_BIN_COUNT; bin++) {
                    const iconName = `arrowIcon${mediaIdx + 1}_y${bin}`;
                    if (map.hasImage(iconName)) { loadedCount++; if (loadedCount === totalToLoad) setYearIconsReady(true); continue; }
                    const image = new Image(24, 24);
                    image.src = iconSrc;
                    image.onload = () => {
                        if (map.hasImage(iconName)) { loadedCount++; if (loadedCount === totalToLoad) setYearIconsReady(true); return; }
                        map.addImage(iconName, { width: 24, height: 24, data: recolorArrow(image, colorForBin(bin, YEAR_BIN_COUNT)) });
                        loadedCount++;
                        if (loadedCount === totalToLoad) setYearIconsReady(true);
                    };
                }
            });
        };

        loadIcons();
        // Re-run on every subsequent style swap (e.g. satellite ↔ default map).
        map.on('style.load', loadIcons);
        return () => { map.off('style.load', loadIcons); };
    }, [mapRef, useYearBins]);

    const featureCollection = {
        type: 'FeatureCollection',
        features: filteredItems
    };

    // Build the icon-image expression
    let iconImageExpr;
    if (useYearBins && yearIconsReady) {
        // Compute thresholds: items with year < yearBounds[0]+step → bin 0; ... ; >= yearBounds[1]−step → bin N-1
        const [minY, maxY] = yearBounds;
        const step = (maxY - minY) / YEAR_BIN_COUNT;
        // Build a step expression on year: returns "y0".."y7"
        // Mapbox step needs alternating output/input pairs after the initial output.
        const yearBinExpr = ['step', ['coalesce', ['get', 'year'], minY], 'y0'];
        for (let bin = 1; bin < YEAR_BIN_COUNT; bin++) {
            yearBinExpr.push(minY + step * bin, `y${bin}`);
        }
        // mediatype prefix: arrowIcon1/2/3 ; full icon name = arrowIcon{N}_{yearBin}
        iconImageExpr = [
            'concat',
            ['match',
                ['get', 'mediatype'],
                'image', 'arrowIcon1',
                'video', 'arrowIcon2',
                'audio', 'arrowIcon3',
                'arrowIcon1'
            ],
            '_',
            yearBinExpr,
        ];
    } else {
        iconImageExpr = [
            'match',
            ['get', 'mediatype'],
            'image', 'arrowIcon1',
            'video', 'arrowIcon2',
            'audio', 'arrowIcon3',
            'arrowIcon1'
        ];
    }

    return (
            <Source id="my-data" type="geojson" data={featureCollection}>
                {/* Density heatmap: stays visible up to street-scale zoom on every site. */}
                {useHeatmap && (
                    <Layer
                        id="pointHeatmap"
                        type="heatmap"
                        maxzoom={16}
                        paint={{
                            // Smaller per-point weight so density grows more linearly with point count.
                            'heatmap-weight': 0.1,
                            // Lower base intensity so the ramp doesn't peg at "hottest" everywhere.
                            'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 0.6, 14, 1],
                            // More stops in the cool half, red reserved for genuinely dense (>0.9) areas.
                            'heatmap-color': [
                                'interpolate', ['linear'], ['heatmap-density'],
                                0,    'rgba(33,102,172,0)',
                                0.15, 'rgb(103,169,207)',
                                0.35, 'rgb(171,217,233)',
                                0.55, 'rgb(254,224,144)',
                                0.75, 'rgb(252,141,89)',
                                0.95, 'rgb(178,24,43)',
                            ],
                            // Tighter radii so blobs hug the actual dots instead of bleeding outward.
                            'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 2, 9, 8, 14, 18],
                            'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 14, 0.85, 16, 0],
                        }}
                    />
                )}
                <Layer
                    id="pointLayer"
                    type="symbol"
                    filter={markedItemId ? ['!=', ['get', 'id'], markedItemId] : ['literal', true]}
                    layout={{
                        "icon-image": iconImageExpr,
                        "icon-rotate": ["get", "angle"],
                        // Angle is a compass bearing (north-relative). Align rotation to the
                        // map so the arrow keeps pointing at the same real-world direction
                        // when the user rotates the basemap.
                        "icon-rotation-alignment": "map",
                        'icon-anchor': 'center',
                        'icon-allow-overlap': true,
                        "icon-size": [
                            "interpolate",
                            ["linear"],
                            ["zoom"],
                            10, 0.4,
                            14, 0.8,
                            18, 1.2
                        ]
                    }}
                    paint={useHeatmap ? {
                        'icon-opacity': ['interpolate', ['linear'], ['zoom'], 14, 0, 16, 1],
                    } : {}}
                />
            </Source>
    );
}

export default PointLayer;
